// "Paid, but something is missing" (crm-billing: billing_order_delivery, billing_delivery_problems). Read-only.
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { formatDateTime } from "@/lib/format";
import { Badge } from "@/components/ui/badge";

export interface DeliveryStep {
  kind: "email" | "access" | "class" | "print";
  label: string;
  state: "done" | "processing" | "late" | "failed" | "missing" | "preview_only" | "off" | "waiting" | "paused";
  detail: string;
  updated_at?: string;
  ref_type?: string;
  ref_id?: number;
  contact_id?: number;
}

export interface OrderDelivery {
  order_id: number;
  contact_id: number;
  brand_id: number;
  status: string;
  total_cents: number;
  paid_at: string | null;
  products: string[];
  steps: DeliveryStep[];
  problems: number;
}

export const STEP_STATE: Record<DeliveryStep["state"], [string, "success" | "warning" | "destructive" | "muted" | "default"]> = {
  done: ["Done", "success"],
  processing: ["Under way", "default"],
  late: ["Late", "warning"],
  failed: ["Failed", "destructive"],
  missing: ["Missing", "destructive"],
  preview_only: ["Preview only", "muted"],
  off: ["Switched off", "muted"],
  waiting: ["Waiting on setup", "muted"],
  paused: ["Paused (missed payment)", "muted"],
};

export const STEP_KIND: Record<DeliveryStep["kind"], string> = { email: "Email", access: "Access", class: "Class", print: "Print" };

export const isProblem = (s: DeliveryStep) => s.state === "late" || s.state === "failed" || s.state === "missing";

/** One order's checklist: every promised step with its state and a plain explanation. */
/** actions: buttons for one step (the print job fixes), shown after its text. */
export function DeliveryChecklist({ steps, onlyProblems, actions }: { steps: DeliveryStep[]; onlyProblems?: boolean; actions?: (s: DeliveryStep) => ReactNode }) {
  const shown = onlyProblems ? steps.filter(isProblem) : steps;
  if (shown.length === 0) return <p className="text-sm text-[var(--color-muted-foreground)]">Nothing to deliver for this order.</p>;
  return (
    <ul className="space-y-1.5">
      {shown.map((s, i) => {
        const [label, variant] = STEP_STATE[s.state] ?? [s.state, "muted" as const];
        return (
          <li key={i} className="flex flex-wrap items-start gap-2 text-sm">
            <Badge variant={variant} className="shrink-0">
              {label}
            </Badge>
            <span className="min-w-0 flex-1">
              <span className="font-medium">{s.label}</span>
              <span className="text-[var(--color-muted-foreground)]">. {s.detail}</span>
              {s.ref_type === "fulfillment" && s.ref_id ? <span className="text-xs text-[var(--color-muted-foreground)]"> (print job {s.ref_id})</span> : null}
              {s.contact_id && s.label.includes("(guest)") ? (
                <>
                  {" "}
                  <Link to="/contacts/$contactId" params={{ contactId: String(s.contact_id) }} className="text-xs text-[var(--color-primary)] hover:underline">
                    guest's contact
                  </Link>
                </>
              ) : null}
            </span>
            {s.updated_at && <span className="num whitespace-nowrap text-xs text-[var(--color-muted-foreground)]">{formatDateTime(s.updated_at)}</span>}
            {actions?.(s)}
          </li>
        );
      })}
    </ul>
  );
}
